"use client";

import { superscriptInt } from "@/lib/tool-kit/biology-lab/format";
import { CHART_COLORS } from "@/lib/tool-kit/palette";
import { niceTicks } from "@/lib/tool-kit/stats-descriptive/ticks";

const WIDTH = 640;
const HEIGHT = 340;
const LEFT = 64;
const RIGHT = 20;
const TOP = 16;
const BOTTOM = 46;
const MAX_MARKERS = 24;

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

/**
 * Exponential growth drawn on a logarithmic count axis, where it is a straight line.
 * Open circles mark each doubling; filled circles mark the start and the end.
 */
export function GrowthChart({
  initial,
  final,
  elapsed,
  doublingTime,
  fmt,
  label,
  xLabel,
  yLabel,
}: {
  initial: number;
  final: number;
  elapsed: number;
  doublingTime: number;
  fmt: Format;
  label: string;
  xLabel: string;
  yLabel: string;
}) {
  const low = Math.floor(Math.log10(Math.min(initial, final)));
  const rawHigh = Math.ceil(Math.log10(Math.max(initial, final)));
  const high = rawHigh === low ? low + 1 : rawHigh;
  const xAxis = niceTicks(0, elapsed, 6);
  const plotWidth = WIDTH - LEFT - RIGHT;
  const plotHeight = HEIGHT - TOP - BOTTOM;
  const px = (t: number) =>
    LEFT + ((t - xAxis.lo) / (xAxis.hi - xAxis.lo || 1)) * plotWidth;
  const py = (n: number) =>
    TOP + plotHeight - ((Math.log10(n) - low) / (high - low)) * plotHeight;
  const decades = Array.from({ length: high - low + 1 }, (_, i) => low + i);
  const doublings = Math.min(Math.floor(elapsed / doublingTime), MAX_MARKERS);
  const markers = Array.from({ length: doublings + 1 }, (_, i) => i).filter(
    (i) => i > 0
  );
  return (
    <svg
      aria-label={label}
      className="h-auto w-full text-muted-foreground"
      role="img"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
    >
      {decades.map((decade) => (
        <g key={`y${decade}`}>
          <line
            stroke="currentColor"
            strokeOpacity={0.2}
            x1={LEFT}
            x2={WIDTH - RIGHT}
            y1={py(10 ** decade)}
            y2={py(10 ** decade)}
          />
          <text
            dominantBaseline="middle"
            fill="currentColor"
            fontSize={12}
            textAnchor="end"
            x={LEFT - 8}
            y={py(10 ** decade)}
          >
            {`10${superscriptInt(decade)}`}
          </text>
        </g>
      ))}
      {xAxis.ticks.map((tick) => (
        <g key={`x${tick}`}>
          <line
            stroke="currentColor"
            strokeOpacity={0.2}
            x1={px(tick)}
            x2={px(tick)}
            y1={TOP}
            y2={TOP + plotHeight}
          />
          <text
            fill="currentColor"
            fontSize={12}
            textAnchor="middle"
            x={px(tick)}
            y={TOP + plotHeight + 18}
          >
            {fmt(tick, { maximumSignificantDigits: 3 })}
          </text>
        </g>
      ))}
      <text
        fill="currentColor"
        fontSize={12}
        textAnchor="middle"
        x={LEFT + plotWidth / 2}
        y={HEIGHT - 6}
      >
        {xLabel}
      </text>
      <text
        fill="currentColor"
        fontSize={12}
        textAnchor="middle"
        transform={`rotate(-90 14 ${TOP + plotHeight / 2})`}
        x={14}
        y={TOP + plotHeight / 2}
      >
        {yLabel}
      </text>
      <line
        stroke={CHART_COLORS[0]}
        strokeWidth={2.5}
        x1={px(0)}
        x2={px(elapsed)}
        y1={py(initial)}
        y2={py(final)}
      />
      {markers.map((i) => (
        <circle
          cx={px(i * doublingTime)}
          cy={py(initial * 2 ** i)}
          fill="none"
          key={`d${i}`}
          r={4}
          stroke={CHART_COLORS[1]}
          strokeWidth={2}
        />
      ))}
      <circle cx={px(0)} cy={py(initial)} fill={CHART_COLORS[0]} r={5} />
      <circle cx={px(elapsed)} cy={py(final)} fill={CHART_COLORS[0]} r={5} />
    </svg>
  );
}
